/**
 * P-01 «Движ» (изменение v1.3) — подборка людей, с которыми есть что делать вместе.
 *
 * ПС §09 / BS §11.4: колода карточек во весь экран, поверх фото — ответ на
 * вопрос «почему мы сойдёмся»: значок совместимости, фраза от подбора, общие
 * интересы. Линзы в шапке («Для тебя», «Рядом», «Онлайн», «Новые», интересы)
 * дают разные срезы одного подбора. «Го» вправо, «Мимо» влево — свайп делает
 * то же самое. Значок совместимости открывает «Почему вы сойдётесь» (P-12):
 * лист на mobile, боковая панель на desktop (там же живут фильтры P-03).
 * P-06/P-07 — пустые состояния объясняют причину и предлагают конкретный шаг.
 * Выдача переживает уход с экрана (discovery-store).
 */
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { CandidateCard, CandidateCardSkeleton, type ViewerContext } from '@entities/candidate/CandidateCard'
import { VibePanel } from '@entities/candidate/VibePanel'
import { profileVisibility } from '@entities/user/visibility'
import { DISCOVERY_MODE, useDiscoveryStore } from '@features/discovery/discovery-store'
import { DEFAULT_LENS, lensLabel, lensOptions } from '@features/discovery/lenses'
import {
  AGE_MAX,
  AGE_MIN,
  describeMissing,
  fromUserMode,
  toModeBody,
} from '@features/discovery/mode-preferences'
import { useDiscovery, type Discovery } from '@features/discovery/use-discovery'
import { api, unwrap } from '@shared/api/client'
import type { Interest, Match, UserCard, UserMode } from '@shared/api/types'
import { draftsRepo, hintsRepo } from '@shared/db'
import { haptic } from '@shared/lib/motion'
import { MEDIA, useIsDesktopLayout, useMediaQuery } from '@shared/lib/use-media-query'
import { ActionItem, ActionList, Banner, Button, EmptyState, Icon, Sheet, cx, useToast } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { refreshMe } from '../../boot/bootstrap'
import { selectIsReadOnly, useAppStore } from '../../model/app-store'
import { offerPushPrompt } from '../../model/push-prompt'
import { MatchCelebration } from '../matches/MatchCelebration'
import { VisibilityCard } from '../profile/VisibilityCard'
import { LensBar } from './LensBar'
import styles from './dvizh.module.css'

/** Сколько карточек колоды смонтировано: верхняя и одна под ней. */
const STACK_DEPTH = 2

interface Celebration {
  match: Match
  /** Первые фразы из подбора той карточки, по которой нажали «Го». */
  icebreakers: readonly string[]
}

export function DvizhScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()

  const me = useAppStore((s) => s.me)
  const readOnly = useAppStore(selectIsReadOnly)

  const discovery = useDiscovery()
  // На телефоне линзы стоят в шапке вместо заголовка — колоде нужна вся высота.
  const isMobile = useMediaQuery(MEDIA.mobile)
  const isDesktop = useIsDesktopLayout()

  const [celebration, setCelebration] = useState<Celebration | null>(null)
  const [actionsFor, setActionsFor] = useState<{ userId: string; name: string; anchor: DOMRect } | null>(null)
  /** Карточка, для которой открыт лист «Почему вы сойдётесь» (mobile/tablet). */
  const [whyFor, setWhyFor] = useState<UserCard | null>(null)
  /** P-13: параметры подбора — отдельный экран, а не слой над колодой. */
  const openSetup = useCallback(() => void navigate({ to: '/dvizh/setup' }), [navigate])
  /**
   * Подсказка о свайпе показывается один раз: жест — главный способ листать колоду,
   * но сам по себе он невидим, а кнопки в рейке выглядят как единственный путь.
   */
  const [coach, setCoach] = useState(false)
  useEffect(() => {
    let alive = true
    void hintsRepo.wasSeen('deck_swipe').then((seen) => {
      if (alive && !seen) setCoach(true)
    })
    return () => {
      alive = false
    }
  }, [])

  const dismissCoach = useCallback(() => {
    setCoach(false)
    void hintsRepo.markSeen('deck_swipe')
  }, [])

  const friendsMode = me?.modes.find((m) => m.mode === DISCOVERY_MODE) ?? null
  const myInterests: readonly Interest[] = me?.profile.interests ?? []
  const viewer: ViewerContext = {
    interest_ids: myInterests.map((i) => i.id),
    formats: friendsMode?.formats ?? [],
    rhythm: friendsMode?.rhythm ?? [],
  }
  const visibility = me ? profileVisibility(me) : null
  // Причины, которые колода объясняет сама (P-07) — их не дублирует полоса статуса.
  const deckExplainsHidden = discovery.modeDisabled || discovery.missingRequirements.length > 0
  const lenses = lensOptions(myInterests)
  const top = discovery.items[0] ?? null

  const handleLike = useCallback(
    async (userId: string) => {
      // BS §6.3 / SYS-16: при санкции «Го» не отправляется, и причина называется.
      if (readOnly) {
        toast.show('Действия ограничены по решению модерации', { tone: 'danger' })
        return
      }

      const icebreakers = discovery.items.find((c) => c.id === userId)?.vibe?.icebreakers ?? []
      const match = await discovery.like(userId)
      if (match) setCelebration({ match, icebreakers })
    },
    [discovery, readOnly, toast],
  )

  const copyIcebreaker = useCallback(
    (text: string) => {
      void navigator.clipboard
        .writeText(text)
        .then(() => {
          haptic('light')
          toast.show('Фраза скопирована')
        })
        .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
    },
    [toast],
  )

  /**
   * Открыть диалог совпадения; фраза из подбора становится черновиком, отправка — явным действием.
   * Черновик пишется ДО закрытия слоя: закрытие M-02 снимает свою запись истории (`history.back()`),
   * и переход, запущенный позже этого, откатился бы вместе с ней.
   */
  const openMatchChat = async (match: Match, draft?: string): Promise<void> => {
    const conversationId = match.conversation_id
    if (!conversationId) {
      setCelebration(null)
      toast.show('Диалог ещё создаётся, попробуйте через секунду')
      return
    }
    if (draft) {
      try {
        await draftsRepo.save({
          conversation_id: conversationId,
          text: draft,
          reply_to_id: null,
          editing_message_id: null,
          attachments: [],
        })
      } catch {
        // Черновик — удобство, а не условие: диалог открывается в любом случае
        toast.show('Не удалось подставить фразу — напишите её сами', { tone: 'danger' })
      }
    }
    setCelebration(null)
    void navigate({ to: '/chats/$conversationId', params: { conversationId } })
  }

  const lensBar = (
    <LensBar
      options={lenses}
      value={discovery.lens}
      onChange={(lens) => discovery.setLens(lens)}
      className={isMobile ? styles['lensBarHeader'] : undefined}
    />
  )

  const setupButton = <Button icon="sliders" variant="ghost" aria-label="Подбор" onClick={openSetup} />

  return (
    <>
      <div className={styles['layout']}>
        <Screen
          title="Движ"
          actions={setupButton}
          headerContent={isMobile ? lensBar : undefined}
          subbar={isMobile ? undefined : <div className={styles['headerRow']}>{lensBar}</div>}
          flush
          className={styles['screen']}
          contentClassName={styles['content']}
        >
          {/*
            Анкета скрыта — говорим об этом там, где человек ждёт совпадений (единый статус).
            Но только о том, о чём молчит сама колода: про выключенный подбор и незаполненные
            параметры она говорит пустым состоянием P-07, и полоса сверху повторяла бы его
            слово в слово.
          */}
          {visibility && !visibility.visible && !deckExplainsHidden ? (
            <div className={styles['notice']}>
              <VisibilityCard compact />
            </div>
          ) : null}

          {discovery.error ? (
            <Banner
              tone="danger"
              className={styles['notice']}
              actions={
                <Button size="sm" variant="secondary" onClick={() => discovery.reload()}>
                  Повторить
                </Button>
              }
            >
              {discovery.error}
            </Banner>
          ) : null}

          <DvizhBody
            discovery={discovery}
            viewer={viewer}
            coach={coach}
            onCoachDone={dismissCoach}
            myInterests={myInterests}
            friendsMode={friendsMode}
            onLike={handleLike}
            onOpen={(userId) => void navigate({ to: '/users/$userId', params: { userId } })}
            onMore={(userId, name, anchor) => setActionsFor({ userId, name, anchor })}
            // На desktop объяснение уже стоит в боковой панели — лист нужен только на узком экране.
            onWhy={(card) => (isDesktop ? undefined : setWhyFor(card))}
            onOpenSetup={openSetup}
          />
        </Screen>

        {/* Desktop: справа — «Почему вы сойдётесь» (P-12) для верхней карточки */}
        {isDesktop ? (
          <aside className={styles['whyAside']} aria-label="Почему вы сойдётесь">
            <div className={styles['asideHead']}>
              <h2 className={styles['asideTitle']}>
                {top?.vibe && top.display_name
                  ? `Почему вы сойдётесь — ${top.display_name}`
                  : 'Почему вы сойдётесь'}
              </h2>
            </div>
            <div className={styles['asideBody']}>
              {top?.vibe ? (
                <VibePanel
                  vibe={top.vibe}
                  card={top}
                  myInterests={myInterests}
                  onCopyIcebreaker={copyIcebreaker}
                />
              ) : (
                <p className={styles['asidePlaceholder']}>
                  Объяснение появится, когда в колоде будет карточка.
                </p>
              )}
            </div>
          </aside>
        ) : null}
      </div>

      {/* P-12 на mobile/tablet: лист поверх колоды */}
      <Sheet
        open={whyFor !== null}
        onClose={() => setWhyFor(null)}
        title="Почему вы сойдётесь"
        description={whyFor?.display_name ?? undefined}
      >
        {whyFor?.vibe ? (
          <VibePanel
            vibe={whyFor.vibe}
            card={whyFor}
            myInterests={myInterests}
            onCopyIcebreaker={copyIcebreaker}
          />
        ) : null}
      </Sheet>

      {/* P-05: действия с карточкой — bottom sheet на mobile, pop-over на desktop */}
      <Sheet
        open={actionsFor !== null}
        onClose={() => setActionsFor(null)}
        title={actionsFor?.name}
        anchorRect={actionsFor?.anchor ?? null}
        flush
      >
        <ActionList>
          <ActionItem
            icon="user"
            label="Открыть профиль"
            onClick={() => {
              const userId = actionsFor?.userId
              setActionsFor(null)
              if (userId) void navigate({ to: '/users/$userId', params: { userId } })
            }}
          />
          <ActionItem
            icon="flag"
            label="Пожаловаться"
            onClick={() => {
              const userId = actionsFor?.userId
              setActionsFor(null)
              if (userId) {
                void navigate({ to: '/users/$userId', params: { userId }, search: { action: 'report' } })
              }
            }}
          />
          <ActionItem
            icon="block"
            label="Заблокировать"
            danger
            onClick={() => {
              const userId = actionsFor?.userId
              setActionsFor(null)
              if (userId) {
                void navigate({ to: '/users/$userId', params: { userId }, search: { action: 'block' } })
              }
            }}
          />
        </ActionList>
      </Sheet>

      {/* M-02 «Это движ!» */}
      {celebration ? (
        <MatchCelebration
          match={celebration.match}
          icebreakers={celebration.icebreakers}
          onClose={() => {
            setCelebration(null)
            // §8.1 п. 2: первая точка входа в N-03 — сразу после первого match.
            void offerPushPrompt()
          }}
          onOpenChat={(draft) => void openMatchChat(celebration.match, draft)}
        />
      ) : null}
    </>
  )
}

/* ===================================================================== */
/* Тело: колода, скелетон или пустое состояние                            */
/* ===================================================================== */

interface DvizhBodyProps {
  discovery: Discovery
  viewer: ViewerContext
  coach: boolean
  onCoachDone: () => void
  myInterests: readonly Interest[]
  friendsMode: UserMode | null
  onLike: (userId: string) => void
  onOpen: (userId: string) => void
  onMore: (userId: string, name: string, anchor: DOMRect) => void
  onWhy: (card: UserCard) => void
  /** P-13 «Подбор» — один адрес и для «включить», и для «ослабить условия». */
  onOpenSetup: () => void
}

function DvizhBody({
  discovery,
  viewer,
  coach,
  onCoachDone,
  myInterests,
  friendsMode,
  onLike,
  onOpen,
  onMore,
  onWhy,
  onOpenSetup,
}: DvizhBodyProps): React.ReactElement {
  const deckRef = useRef<HTMLDivElement>(null)
  const top = discovery.items[0]

  /*
   * На desktop свайпа нет, а тянуться мышью к рейке каждый раз утомительно: стрелки
   * делают то же самое. Работает, только когда фокус не в поле ввода и не открыт слой.
   */
  useEffect(() => {
    if (!top || discovery.busyId) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return
      if (document.querySelector('[data-modal-layer]')) return
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        onLike(top.id)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        void discovery.pass(top.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [discovery, onLike, top])

  /*
   * Ход жеста верхней карточки подтягивает следующую и красит сцену: `--deck-progress` —
   * насколько далеко ушла карточка, `--deck-dir` — в какую сторону. Колода живёт, а не подменяется.
   */
  const onDrag = useCallback((drag: number) => {
    const deck = deckRef.current
    if (!deck) return
    deck.style.setProperty('--deck-progress', String(Math.abs(drag)))
    deck.style.setProperty('--deck-dir', String(Math.sign(drag)))
  }, [])

  // Подбор выключен → P-07 с переходом к настройкам.
  if (discovery.modeDisabled) {
    return (
      <EmptyState
        icon="dvizh"
        title="Подбор выключен"
        text="Пока он выключен, подборки нет и вас не показывают."
        actions={
          <Button variant="primary" onClick={onOpenSetup}>
            Включить подбор
          </Button>
        }
      />
    )
  }

  // Обязательные параметры не заполнены → P-07 с перечнем, чего не хватает.
  if (discovery.missingRequirements.length) {
    return (
      <EmptyState
        icon="sliders"
        title="Заполните параметры подбора"
        text={`Не хватает: ${describeMissing(discovery.missingRequirements)}`}
        actions={
          <Button variant="primary" onClick={onOpenSetup}>
            Заполнить
          </Button>
        }
      />
    )
  }

  if (!discovery.loaded || (discovery.loading && !discovery.items.length)) {
    return (
      <div className={styles['stage']} aria-busy>
        <div className={styles['deck']}>
          <CandidateCardSkeleton className={styles['deckCard']} />
        </div>
      </div>
    )
  }

  // Ошибка без единой карточки — объяснение уже над колодой, тут только место для повтора.
  if (discovery.error && !discovery.items.length) {
    return (
      <EmptyState
        icon="wifi-off"
        title="Подборка не загрузилась"
        text="Проверьте соединение и попробуйте снова."
        actions={
          <Button variant="primary" onClick={() => discovery.reload()}>
            Повторить
          </Button>
        }
      />
    )
  }

  // Линза опустела — это не повод ослаблять фильтры: предлагаем вернуться к «Для тебя».
  if (!discovery.items.length && discovery.lens !== DEFAULT_LENS) {
    return (
      <EmptyState
        icon="search"
        title={`В линзе «${lensLabel(discovery.lens, myInterests)}» пока никого`}
        text="В подборке «Для тебя» — все, кто подходит."
        actions={
          <div className={styles['relaxActions']}>
            <Button variant="primary" onClick={() => discovery.setLens(DEFAULT_LENS)}>
              Открыть «Для тебя»
            </Button>
            <Button variant="ghost" onClick={() => discovery.reload()}>
              Обновить
            </Button>
          </div>
        }
      />
    )
  }

  // P-06/P-07: кандидаты закончились. ПС §09 — объяснить и предложить,
  // что именно ослабить; НЕ подменять пустоту случайными профилями.
  if (!discovery.items.length) {
    return (
      <RelaxHints
        hints={discovery.relaxHints}
        friendsMode={friendsMode}
        onOpenSetup={onOpenSetup}
        onReload={() => discovery.reload()}
      />
    )
  }

  // Верхняя карточка — первая в DOM (и для скринридера, и для тестов); поверх следующей её держит z-index
  const stack = discovery.items.slice(0, STACK_DEPTH)

  return (
    <>
      {/* Подсказка о жесте — строкой над сценой: она ничего не перекрывает и уходит после первого свайпа */}
      {coach ? (
        <div className={styles['coachBar']}>
          <span className={styles['coachText']}>
            <Icon name="back" size={14} />
            Листайте пальцем: вправо — «Го», влево — «Мимо»
            <Icon name="forward" size={14} />
          </span>
          <button type="button" className={styles['coachClose']} onClick={onCoachDone}>
            Понятно
          </button>
        </div>
      ) : null}

      <div ref={deckRef} className={styles['stage']}>
        <div className={styles['deck']}>
          {stack.map((card, index) => {
            const top = index === 0
            return (
              <CandidateCard
                key={card.id}
                card={card}
                viewer={viewer}
                pick={discovery.lens === DEFAULT_LENS && discovery.pickId === card.id}
                className={cx(styles['deckCard'], top ? styles['deckTop'] : styles['deckNext'])}
                interactive={top}
                coach={top && coach}
                busy={discovery.busyId === card.id}
                // Карточка гаснет, пока «Го» или пропуск в пути; при ошибке возвращается
                leaving={discovery.busyId === card.id}
                onLike={() => onLike(card.id)}
                onPass={() => void discovery.pass(card.id)}
                onOpen={() => onOpen(card.id)}
                onMore={(anchor) => onMore(card.id, card.display_name ?? 'Профиль', anchor)}
                onWhy={() => onWhy(card)}
                onDrag={top ? onDrag : undefined}
                onGestureStart={coach ? onCoachDone : undefined}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}

/* ===================================================================== */
/* P-06: подсказки ослабить условия — действиями, а не списком            */
/* ===================================================================== */

/** Шаг расширения возраста за одно нажатие (лет в каждую сторону). */
const AGE_STEP = 5

function RelaxHints({
  hints,
  friendsMode,
  onOpenSetup,
  onReload,
}: {
  hints: readonly string[]
  friendsMode: UserMode | null
  onOpenSetup: () => void
  onReload: () => void
}) {
  const toast = useToast()
  const invalidate = useDiscoveryStore((s) => s.invalidate)
  const [busy, setBusy] = useState<string | null>(null)

  /** Применить подсказку сразу: расширить диапазон и перезапросить колоду. */
  const relax = async (hint: 'age_range', target: UserMode): Promise<void> => {
    const value = fromUserMode(target)
    const next = {
      ...value,
      age: [Math.max(AGE_MIN, value.age[0] - AGE_STEP), Math.min(AGE_MAX, value.age[1] + AGE_STEP)] as [
        number,
        number,
      ],
    }

    setBusy(hint)
    try {
      unwrap(
        await api.PUT('/me/modes/{mode}', {
          params: { path: { mode: target.mode } },
          body: toModeBody(next, target.enabled) as never,
        }),
      )
      await refreshMe()
      invalidate()
      toast.show(`Возраст: ${next.age[0]}–${next.age[1]}`)
    } catch {
      toast.show('Не удалось изменить параметры', { tone: 'danger' })
    } finally {
      setBusy(null)
    }
  }

  const actions: React.ReactNode[] = []
  const canWidenAge =
    friendsMode && (fromUserMode(friendsMode).age[0] > AGE_MIN || fromUserMode(friendsMode).age[1] < AGE_MAX)

  if (hints.includes('age_range') && friendsMode && canWidenAge) {
    actions.push(
      <Button
        key="age"
        variant="secondary"
        loading={busy === 'age_range'}
        onClick={() => void relax('age_range', friendsMode)}
      >
        Расширить возраст на ±{AGE_STEP} лет
      </Button>,
    )
  }
  // Изменение v1.5: города не «расширяются» сами — человек выбирает их сам в фильтрах
  if (hints.includes('cities') && friendsMode) {
    actions.push(
      <Button key="cities" variant="secondary" onClick={onOpenSetup}>
        Добавить города
      </Button>,
    )
  }

  const passive = hints.filter((hint) => hint === 'languages' || hint === 'interests')

  return (
    <EmptyState
      icon="dvizh"
      title="Пока больше некого показать"
      text={
        actions.length || passive.length
          ? 'Ослабьте условия — появятся новые.'
          : 'Загляните позже: новые люди появляются каждый день.'
      }
      actions={
        <div className={styles['relaxActions']}>
          {actions}
          {passive.length ? (
            <div className={styles['hintList']}>
              {passive.map((hint) => (
                <span key={hint} className={styles['hintItem']}>
                  <Icon name="chevron-right" size={16} />
                  {hint === 'languages' ? 'Уберите ограничение по языкам' : 'Уберите фильтр по интересам'}
                </span>
              ))}
            </div>
          ) : null}
          <Button variant="primary" onClick={onOpenSetup}>
            Открыть параметры подбора
          </Button>
          <Button variant="ghost" onClick={onReload}>
            Обновить
          </Button>
        </div>
      }
    />
  )
}
